// Vector port of TournamentPrintableTable.js's renderSchedule(). Every
// match cell follows the same fixed template (stage line, side1, "vs",
// side2, optional score) so - unlike a free-flowing text wrap - rows can
// use one fixed height instead of a per-row measure pass.
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { getGroupRoundNumber, getOrdinalSuffix, getRoundTitle, groupCharCode } from "../../../constants";
import { getMatchParticipantNames, getMatchScoreText } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawTableRow, hLine, drawText } from "./pdfKit";

const HEADER_FS = px2pt(11);
const META_FS = px2pt(11);
const NAME_FS = px2pt(11);
const VS_FS = px2pt(10);
const SCORE_FS = NAME_FS * 1.1;
const HEADER_H = lineHeightMm(HEADER_FS, 1.1) * 2 + px2mm(16);
const ROW_H = px2mm(10) /* pad */ + lineHeightMm(META_FS, 1.3) + lineHeightMm(NAME_FS, 1.25) + lineHeightMm(VS_FS, 1.15) + lineHeightMm(NAME_FS, 1.25) + lineHeightMm(SCORE_FS, 1.3);

const ROUND_COL_PX = 90;
const COURT_COL_PX = 180;

export const scheduleContentWidthMm = (courtsCount) => px2mm(ROUND_COL_PX + Math.max(courtsCount, 1) * COURT_COL_PX);

export const measureScheduleTable = (data) => {
    const { courts = [], rounds = [] } = data || {};
    if (!courts.length && !rounds.length) return px2mm(40);
    const rowsCount = new Set(rounds.map(r => r.order)).size;
    return HEADER_H + rowsCount * ROW_H;
};

const drawMatchCell = (pdf, cx, cy, w, match, grade, i18n) => {
    const { side1, side2 } = getMatchParticipantNames(match, grade);
    const scoreText = getMatchScoreText(match);
    const groupRound = getGroupRoundNumber(match.total, match.match_order);
    const stageDesc = match.group_number
        ? `${t("Group")} ${groupCharCode(match.group_number)}${groupRound ? ` (${t("R.")} ${groupRound})` : ""}`
        : `${match.draw_number ? `${match.draw_number}${getOrdinalSuffix(match.draw_number)} ${t("Place")} · ` : ""}${getRoundTitle(match.round_number, match.total, t, i18n, match.match_order, false)}`;

    const padX = px2mm(8);
    let ty = cy + px2mm(5) + META_FS * 0.352778 * 0.85;

    pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
    pdf.setFontSize(META_FS);
    pdf.setTextColor(T.muted);
    pdf.text(stageDesc, cx + padX, ty, { maxWidth: w - padX * 2 });
    if (match.match_number) {
        pdf.text(`#${match.match_number}`, cx + w - padX, ty, { align: "right" });
    }
    hLine(pdf, cx + padX, ty + px2mm(2.5), w - padX * 2, T.border, px2mm(0.5));
    ty += lineHeightMm(META_FS, 1.3);

    const side1Win = match.winner_side === 1;
    const side2Win = match.winner_side === 2;
    // No green in the PDF - winner reads as black/bold instead of colored.
    pdf.setFont(pdf.__pdfFontName || "PTSans", side1Win ? "bold" : "normal");
    pdf.setFontSize(NAME_FS);
    pdf.setTextColor(T.ink);
    pdf.text(`${side1Win ? "✓ " : ""}${side1}`, cx + padX, ty + lineHeightMm(NAME_FS, 1.25) * 0.78, { maxWidth: w - padX * 2 });
    ty += lineHeightMm(NAME_FS, 1.25);

    pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
    pdf.setFontSize(VS_FS);
    pdf.setTextColor(T.muted);
    pdf.text("vs", cx + padX, ty + lineHeightMm(VS_FS, 1.15) * 0.78);
    ty += lineHeightMm(VS_FS, 1.15);

    pdf.setFont(pdf.__pdfFontName || "PTSans", side2Win ? "bold" : "normal");
    pdf.setFontSize(NAME_FS);
    pdf.setTextColor(T.ink);
    pdf.text(`${side2Win ? "✓ " : ""}${side2}`, cx + padX, ty + lineHeightMm(NAME_FS, 1.25) * 0.78, { maxWidth: w - padX * 2 });
    ty += lineHeightMm(NAME_FS, 1.25);

    if (scoreText) {
        hLine(pdf, cx + padX, ty + px2mm(1), w - padX * 2, T.border, px2mm(0.5));
        pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
        pdf.setFontSize(SCORE_FS);
        pdf.setTextColor(T.ink);
        pdf.text(scoreText, cx + w - padX, ty + lineHeightMm(SCORE_FS, 1.3) * 0.85, { align: "right" });
    }
};

export const drawScheduleTable = (pdf, x, y, data, grade, i18n) => {
    const { courts = [], rounds = [] } = data || {};
    const contentWidthMm = scheduleContentWidthMm(courts.length);
    let cy = y;

    if (!courts.length && !rounds.length) {
        pdf.setFillColor(T.superWhite);
        pdf.rect(x, cy, contentWidthMm, px2mm(40), "F");
        drawText(pdf, t("No data available"), x + contentWidthMm / 2, cy + px2mm(15), { fontSizePt: HEADER_FS, color: T.muted, align: "center" });
        return cy + px2mm(40);
    }

    const roundColW = px2mm(ROUND_COL_PX);
    const courtColW = px2mm(COURT_COL_PX);

    const headerCols = [
        { widthMm: roundColW, align: "center", customDraw: (pdf2, cx, cyy) => {
            pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
            pdf2.setFontSize(HEADER_FS);
            pdf2.setTextColor(T.ink);
            const headerLh = lineHeightMm(HEADER_FS, 1.1);
            pdf2.text(t("Round").toUpperCase(), cx + roundColW / 2, cyy + HEADER_H / 2 - headerLh * 0.15, { align: "center" });
            pdf2.text(t("Time").toUpperCase(), cx + roundColW / 2, cyy + HEADER_H / 2 + headerLh * 0.85, { align: "center" });
        } },
        ...courts.map(c => ({ widthMm: courtColW, align: "center", text: `${t("Court")} ${c.number || c.name || ""}`.toUpperCase() })),
    ];
    hLine(pdf, x, cy, contentWidthMm, T.border, px2mm(1));
    drawTableRow(pdf, { x, y: cy, totalWidthMm: contentWidthMm, cols: headerCols, rowHeightMm: HEADER_H, bg: T.superWhite, borderColor: T.border, fontSizePt: HEADER_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
    cy += HEADER_H;

    const rows = {};
    rounds.forEach(round => {
        if (!rows[round.order]) rows[round.order] = {};
        rows[round.order][round.court] = round;
    });

    Object.keys(rows).forEach((order, index) => {
        const rowCells = Object.values(rows[order]);
        const firstRoundInRow = rowCells[0];
        const timeStr = firstRoundInRow?.start_time || "";

        const cols = [
            { widthMm: roundColW, align: "center", customDraw: (pdf2, cx, cyy) => {
                pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                pdf2.setFontSize(META_FS);
                pdf2.setTextColor(T.ink);
                const metaLh = lineHeightMm(META_FS, 1.2);
                const numY = timeStr ? cyy + ROW_H / 2 - metaLh * 0.15 : cyy + ROW_H / 2 + META_FS * 0.352778 * 0.32;
                pdf2.text(`№ ${index + 1}`, cx + roundColW / 2, numY, { align: "center" });
                if (timeStr) {
                    pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                    pdf2.setFontSize(px2pt(10));
                    pdf2.setTextColor(T.muted);
                    pdf2.text(timeStr, cx + roundColW / 2, cyy + ROW_H / 2 + metaLh * 0.85, { align: "center" });
                }
            } },
            ...courts.map(court => {
                const round = rows[order]?.[court.id];
                const match = round?.match;
                if (!match) {
                    return { widthMm: courtColW, align: "center", text: "—", color: T.muted, fontSizePt: META_FS };
                }
                return { widthMm: courtColW, customDraw: (pdf2, cx, cyy, w) => drawMatchCell(pdf2, cx, cyy, w, match, grade, i18n) };
            }),
        ];

        drawTableRow(pdf, { x, y: cy, totalWidthMm: contentWidthMm, cols, rowHeightMm: ROW_H, bg: index % 2 === 0 ? T.superWhite : T.white, borderColor: T.border, fontSizePt: META_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
        cy += ROW_H;
    });

    return cy;
};
